import { GlossDateAbstraction } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.abstraction";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { DetailedBalance } from "../../types/balanceGroupingTypes";
import { Origin, TransactionStatusType } from "../../types/general-types";
import { GlossQuantity, GlossQuantityUtils } from "../../data/shared/gloss-quantity";
import { Valuation } from "../../data/valuation.data";
import { TransactionDirection } from "../../enum/transactionDirection";
import { ModelView } from "@bitwarden/web-vault/app/models/view/model.view";
import {
  DefaultTransactionStoreModel,
  TransactionModelVersion,
  TransactionStoreModel,
} from "@bitwarden/web-vault/app/models/store/transaction.store.model";
import {
  enrichTransaction,
  enrichTransactionDate,
} from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";

import {
  LinkedProperty,
  LinkedView,
  TransactionLinkedProperties,
} from "@bitwarden/web-vault/app/models/view/linked.property";
import {
  AllocationCalculatedProperties,
  BalanceCalculatedProperties,
  CalculatedProperty,
  RevaluationCalculatedProperties,
  ValuationCalculatedProperties,
} from "@bitwarden/web-vault/app/models/view/calculated.property";
import {
  TransactionLinkType,
  TransactionOrigin,
  TransactionStatus,
} from "@bitwarden/web-vault/app/models/types/transaction.types";
import { Allocation } from "@bitwarden/web-vault/app/models/store/allocations/allocation.class";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";
import { AllocationFactory } from "@bitwarden/web-vault/app/models/store/allocations/allocation.factory";
import { NestedAllocationsType } from "@bitwarden/web-vault/app/models/store/allocations/allocation.type";

export class TransactionView
  extends ModelView<TransactionStoreModel>
  implements
    LinkedProperty<TransactionLinkedProperties>,
    CalculatedProperty<RevaluationCalculatedProperties>,
    CalculatedProperty<BalanceCalculatedProperties>,
    CalculatedProperty<AllocationCalculatedProperties>,
    CalculatedProperty<ValuationCalculatedProperties>
{
  protected version: TransactionModelVersion;

  /** @deprecated **/
  sourceId: string; // todo use LinkedView
  /** use the new link proterties type **/
  sourceLink: LinkedView = { id: "" };

  /** transaction origin: manual? basiq? plaid? **/
  origin: TransactionOrigin;

  /** @deprecated **/
  accountId: string; // todo use LinkedView
  /** use the new link proterties type **/
  accountLink: LinkedView = { id: "" };

  /**
   * Use for filtering, those are calculated properties in the Transaction Store
   * @tag Transaction Store
   */
  c_institutionLink: LinkedView = { id: "" };

  /**
   * Use for filtering, those are calculated properties in the Transaction Store
   * @tag Transaction Store
   */
  c_country: LinkedView = { id: "" };

  bankImportedBalance: number;
  bankImportedBalanceOrigin: Origin;

  allocations: Allocation[] = AllocationUtils.createDefaultAllocations();

  // Calculated allocations
  c_allocationsNested: NestedAllocationsType; // calculated from the allocations for display purposes
  c_allocationsNormalized: NestedAllocationsType; // normalised to the core currency
  c_categories: string[] = []; // calculated from the allocations for filtering purposes
  c_classifications: string[] = []; // calculated from the allocations for filtering purposes

  description: string;
  direction: TransactionDirection;
  linkedTo: Array<string> = []; // todo use LinkedView

  /** Ids the user has explicitly unlinked from this transaction. Persisted (xLid)
      so auto-detection never re-links a pair the user has rejected. */
  excludedLinkIds: Array<string> = [];

  /**
   * Auto-detected transfer/conversion links, recomputed on every enrichment
   * cycle by TransactionConversionService. Never persisted - deliberate links
   * live in `linkedTo`.
   * @tag Transaction Store
   */
  c_linkedTo: Array<string> = [];

  /**
   * Classification of the auto-detected link in `c_linkedTo`.
   * @tag Transaction Store
   */
  c_linkType: TransactionLinkType | null = null;

  estimateTransaction: boolean;
  quantity: GlossQuantity;
  glossPostDate: GlossDateAbstraction;
  glossTransactionDate: GlossDateAbstraction;
  definition: TransactionStatusType;
  status: TransactionStatus;
  postDate: Date;
  transactionDate: Date;
  kind: string;
  isDuplicate: boolean;

  // use this field to store the value of a stock if one is supplied at transaction
  // creation time or when importing. The system will also create a reference data for it
  // but we also store it to use in the transaction so we know it come together at import time.
  valuationPrice: number;

  // @deprecated
  isSelected = false;

  /** @deprecated **/
  valuation: Valuation;

  /**
   * Use for filtering, those are calculated properties in the Transaction Store
   * @tag Calculation Store
   */
  c_valuation: Valuation; // contains the normalisation information of the transaction

  /** Calculated: Allocation */
  /** @deprecated **/
  // allocations: Allocation[];
  // c_allocations: Allocation[];
  /** Calculated: Balance */
  /** @deprecated **/
  balance: number;
  c_balance: number;

  /** @deprecated **/
  dailyBalance: number;

  /** @deprecated **/
  detailedBalance: DetailedBalance;

  /** Calculated: Revaluation */
  /** @deprecated **/
  revalTransaction: boolean;
  c_revalTransaction: boolean;

  /** use this property now **/
  c_isRevaluation: boolean;

  isDeleted: boolean;

  /**
   * Set only on synthetic "starting_alignment" rows created by
   * BalanceAlignment.getOpeningBalanceOnFirstTransaction. Records the definition (opening/
   * closing/manual_balance/etc) of the actual raw transaction that supplied this row's balance,
   * since starting_alignment's own `definition` can't say whether it represents an opening or
   * closing balance.
   * @tag Calculation Store
   */
  c_startingAlignmentSourceDefinition: TransactionStatusType;

  constructor(transactionModel?: TransactionStoreModel) {
    super(transactionModel);
    if (!transactionModel) {
      // for legacy constructor we need to set the version for dali structure
      this.version = DefaultTransactionStoreModel.v;
      this.allocations = AllocationUtils.createDefaultAllocations();
      return;
    }

    if (typeof transactionModel === "object") {
      this.c_country = { id: "" };
      this.sourceId = transactionModel.srId;
      this.origin = transactionModel.ori;
      this.sourceLink.id = transactionModel.srId;
      this.accountId = transactionModel.acId;
      this.accountLink.id = transactionModel.acId;
      this.bankImportedBalance = transactionModel.bIB;
      this.bankImportedBalanceOrigin = transactionModel.bIBO;
      // Null-guard `alc` like `xLid` below (`?? []`): a model with a missing/undefined `alc` — e.g. a
      // malformed row echoed back from a write — otherwise throws a TypeError here, and because every
      // model is reconstructed in `TransactionStoreCollection.triggerChanges()` (run inside the delete
      // `finally`), that TypeError would supersede a `PartialTransactionDeleteError` the caller relies
      // on. Treat an absent `alc` as an empty one (default allocations). See #4700.
      if (!transactionModel.alc?.length) {
        this.allocations = AllocationUtils.createDefaultAllocations();
      } else {
        this.allocations = AllocationFactory.createFromAllocationObjectArray(transactionModel.alc);
      }
      this.description = transactionModel.dsc;
      this.direction = transactionModel.dir;
      this.valuationPrice = transactionModel.vlPc;
      this.linkedTo = transactionModel.lTid;
      this.excludedLinkIds = transactionModel.xLid ?? [];
      this.quantity = GlossQuantityUtils.fromModel(transactionModel.qty);
      this.glossPostDate = GlossDateFactory.createGlossDateFromObject(transactionModel.tDt);
      this.glossTransactionDate = transactionModel.pDt
        ? GlossDateFactory.createGlossDateFromObject(transactionModel.pDt)
        : null;
      this.definition = transactionModel.dfn;
      this.kind = transactionModel.knd;
      this.status = transactionModel.sts;
      this.isDeleted = transactionModel.del;
      enrichTransactionDate(this);
    } else {
      this.version = DefaultTransactionStoreModel.v;
      this.allocations = AllocationUtils.createDefaultAllocations();
    }
  }

  toStoreModel(): TransactionStoreModel {
    return {
      dc: this.dateCreated.toISOString(),
      dm: this.dateModified.toISOString(),
      id: this.id,
      v: this.version,
      vid: this.vaultId,
      ori: this.origin,
      srId: this.sourceLink.id,
      acId: this.accountLink.id,
      bIB: this.bankImportedBalance,
      bIBO: this.bankImportedBalanceOrigin,
      alc: this.allocations.map((allocation) => allocation.toAllocationAsObjectType()),
      dsc: this.description,
      dir: this.direction,
      vlPc: this.valuationPrice,
      lTid: this.linkedTo,
      xLid: this.excludedLinkIds,
      qty: {
        qty: {
          amt: this.quantity.actualQuantity.amount,
          sym: this.quantity.actualQuantity.symbol,
          prcs: this.quantity.actualQuantity.precision,
        },
        ccy: this.quantity.currency,
        cvsym: this.quantity.convsym,
        cvrt: this.quantity.convrate,
      },
      tDt: this.glossPostDate.toGlossDateAsObject(),
      pDt: this.glossTransactionDate ? this.glossTransactionDate.toGlossDateAsObject() : null,
      dfn: this.definition,
      knd: this.kind,
      sts: this.status,
    };
  }

  clone(): TransactionView {
    // Cary over the c_ field
    const copy = new TransactionView(this.toStoreModel());
    copy.c_country = { ...this.c_country };
    copy.c_institutionLink = this.c_institutionLink;
    copy.c_isRevaluation = this.c_isRevaluation;
    copy.c_valuation = this.c_valuation?.clone();
    copy.c_balance = this.c_balance;
    copy.c_revalTransaction = this.c_revalTransaction;
    copy.c_linkedTo = [...this.c_linkedTo];
    copy.c_linkType = this.c_linkType;
    copy.c_startingAlignmentSourceDefinition = this.c_startingAlignmentSourceDefinition;

    // copy deprecated prop
    copy.revalTransaction = this.revalTransaction;
    copy.balance = this.balance;
    copy.detailedBalance = this.detailedBalance;
    copy.dailyBalance = this.dailyBalance;
    copy.valuation = this.valuation?.clone();
    enrichTransaction(copy);
    return copy;
  }
}
